/**
 * EditorToolbar — lightweight formatting bar for the writing canvas.
 * Operates directly on the contentEditable element via execCommand, kept
 * deliberately small: no Word-style ribbon.
 */

import React from 'react';
import {
  Bold, Italic, Underline, Heading2, Quote, List, ListOrdered, Link2, Undo2, Redo2,
} from 'lucide-react';
import { cx } from '@/components/site/primitives';

const TOOLS = [
  { key: 'bold', icon: Bold, label: 'Bold (Ctrl+B)', command: 'bold' },
  { key: 'italic', icon: Italic, label: 'Italic (Ctrl+I)', command: 'italic' },
  { key: 'underline', icon: Underline, label: 'Underline', command: 'underline' },
  { key: 'divider1' },
  { key: 'heading', icon: Heading2, label: 'Heading', command: 'formatBlock', value: 'h2' },
  { key: 'quote', icon: Quote, label: 'Quote', command: 'formatBlock', value: 'blockquote' },
  { key: 'divider2' },
  { key: 'ul', icon: List, label: 'Bulleted list', command: 'insertUnorderedList' },
  { key: 'ol', icon: ListOrdered, label: 'Numbered list', command: 'insertOrderedList' },
  { key: 'link', icon: Link2, label: 'Link', command: 'createLink' },
  { key: 'divider3' },
  { key: 'undo', icon: Undo2, label: 'Undo', command: 'undo' },
  { key: 'redo', icon: Redo2, label: 'Redo', command: 'redo' },
];

export default function EditorToolbar({ targetRef, onAfterCommand, className = '' }) {
  const runCommand = (tool) => {
    const el = targetRef?.current;
    if (!el) return;
    el.focus();

    if (tool.command === 'createLink') {
      const url = window.prompt('Link URL');
      if (!url) return;
      document.execCommand('createLink', false, url);
    } else {
      document.execCommand(tool.command, false, tool.value ?? null);
    }
    onAfterCommand?.(el.innerHTML);
  };

  return (
    <div
      className={cx('flex flex-wrap items-center gap-0.5 rounded-full border border-line bg-card px-2 py-1.5 shadow-soft', className)}
      data-testid="my-writing-toolbar"
      role="toolbar"
      aria-label="Formatting"
    >
      {TOOLS.map((tool) => {
        if (tool.key.startsWith('divider')) {
          return <span key={tool.key} className="mx-1 h-4 w-px bg-line" aria-hidden="true" />;
        }
        const Icon = tool.icon;
        return (
          <button
            key={tool.key}
            type="button"
            title={tool.label}
            aria-label={tool.label}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => runCommand(tool)}
            className="grid h-8 w-8 place-items-center rounded-full text-subtle transition-colors hover:bg-cream hover:text-coral"
          >
            <Icon className="h-4 w-4" />
          </button>
        );
      })}
    </div>
  );
}
